import { StandardProps } from "../componentMap";
import { useVariant } from "../../hooks/useVariant/useVariant";
import styles from "./ProductAccordion.module.scss";
import { ProductDescriptionAccordionUI } from "./components/ProductDescriptionAccordion/ProductDescriptionAccordionUI";
import { getDescription } from "../../utils/product/getDescription";
import { ProductSpecificationAccordionUI } from "./components/ProductSpecificationAccordion/ProductSpecificationAccordionUI";
import { sortBy } from "../../utils/sortBy/sortBy";
import { ProductBrandAccordionUI } from "./components/ProductBrandAccordion/ProductBrandAccordionUI";
import { ProductDeliveryOptionsAccordionUI } from "./components/ProductDeliveryOptionsAccordion/ProductDeliveryOptionsAccordionUI";
import { isAvailableToPreOrder } from "../../utils/variant/isAvailableToPreOrder";
import { isInExtendedReturnsWindow } from "../../utils/product/isInExtendedReturnsWindow";
import {
  Attribute,
  AvailableOption,
  UnavailableCollectionOption,
} from "../../types/prodcatBaseTypes";
import { getDeliveryPrice } from "./components/ProductDeliveryOptionsAccordion/util/getDeliveryPrice";
import { ProductReviewsAccordionUI } from "./components/ProductReviewsAccordion/ProductReviewsAccordionUI";
import { useRouter } from "next/router";
import { ensureIsActive } from "../../utils/product/ensureIsActive";
import { isInStoreOnly } from "../../utils/variant/isInStoreOnly";
import { ProductSizeAndFitAccordionUI } from "./components/ProductSizeAndFitAccordion/ProductSizeAndFitAccordionUI";
import { getSizeAndFitAttributes } from "./components/ProductSizeAndFitAccordion/sizeAndFitUtils";
import { ProductSpecialOffersAccordionUI } from "./components/ProductSpecialOffersAccordion/ProductSpecialOffersAccordionUI";
import { promotionalMessagesToShow } from "../PromotionalMessages/promotionalMessagesUtils";
import { BuyingGuidesAccordionUI } from "./components/BuyingGuidesAccordion/BuyingGuidesUI";
import { OutletAccordionUI } from "./components/OutletAccordion/OutletAccordionUI";
import { ProductHowToUseAccordionUI } from "./components/HowToUseAccordion/HowToUseAccordionUI";
import { ProductIngredientsAccordionUI } from "./components/IngredientsAccordion/IngredientsAccordionUI";
import { isOutlet } from "../../utils/variant/isOutlet";
import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery";

type AccordionConfiguration = {
  name:
    | "description"
    | "specification"
    | "howToUse"
    | "ingredients"
    | "buyingGuides"
    | "outlet"
    | "brand"
    | "deliveryOptions"
    | "reviews"
    | "sizeAndFit"
    | "specialOffers";
  open?: boolean;
};

const defaultAccordions: AccordionConfiguration[] = [
  {
    name: "description",
    open: true,
  },
  {
    name: "specification",
  },
  {
    name: "brand",
  },
  {
    name: "deliveryOptions",
  },
  {
    name: "reviews",
  },
];

const transformAvailable = (option: AvailableOption) => ({
  headline: option.headline,
  description: option.description,
  displayPrice: getDeliveryPrice(option),
});

const transformUnavailableCollection = (
  option: UnavailableCollectionOption,
) => ({
  headline: option.headline,
  description: option.description,
  displayPrice: "",
});

export const ProductAccordion = ({
  product,
  runtimeConfig,
  configProps,
  experiencesRawString,
  experiences,
}: Pick<
  StandardProps,
  | "product"
  | "runtimeConfig"
  | "configProps"
  | "experiencesRawString"
  | "experiences"
>) => {
  const { variant, isSelected } = useVariant(product);

  const showExclusionsMessaging =
    experiences.returnsExclusionsMessaging === "on";
  const showOutletReturnMessage = isOutlet(variant);
  const isRetired = !ensureIsActive(product);
  const { saveLabels, newPromoDrawer } = experiences;

  const [endorsement] =
    variant.messaging?.filter((message) => message.type === "endorsement") ||
    [];

  const variantAttributes = variant.attributes || [];
  const primaryAttributes = product.attributes || [];
  const allAttributes = [...primaryAttributes, ...variantAttributes];
  const sortedAttributes = sortBy(allAttributes, (a) => a.displayName || "");

  const available = product.fulfilmentOptions?.available || [];
  const unavailableCollection =
    product.fulfilmentOptions?.unavailableCollection || [];

  const deliveryOptions = [
    ...available.map(transformAvailable),
    ...unavailableCollection.map(transformUnavailableCollection),
  ];

  const { asPath } = useRouter();
  const { reviewsApiKey, reviewsScriptUrl } = runtimeConfig;

  const specialOffers =
    promotionalMessagesToShow(
      product,
      variant,
      isSelected,
      saveLabels === "on",
    )?.filter(({ description }) => description) || [];

  // TODO STIT-2620 validate configProps.accordionConfig here?
  const displayAccordions =
    (configProps?.accordionConfig as Array<AccordionConfiguration>) ||
    defaultAccordions;

  const showSpecialOffersAccordion =
    displayAccordions.some((da) => da.name === "specialOffers") &&
    specialOffers.length > 0;

  let productSpecificationAttributes = sortedAttributes;
  let sizeAndFitAttributes: Attribute[] = [];

  if (displayAccordions.some((da) => da.name === "sizeAndFit")) {
    const categoryIds = product?.browsePageHierarchy?.map(
      (bph) => bph.categoryId,
    );
    sizeAndFitAttributes = getSizeAndFitAttributes(
      categoryIds,
      sortedAttributes,
    );

    if (sizeAndFitAttributes?.length > 0) {
      productSpecificationAttributes = sortedAttributes.filter(
        (attr) => !sizeAndFitAttributes.includes(attr),
      );
      if (!showSpecialOffersAccordion) {
        displayAccordions.forEach(
          (accord) => (accord.open = accord.name === "sizeAndFit"),
        );
      }
    }
  }

  if (showSpecialOffersAccordion) {
    displayAccordions.forEach(
      (accord) => (accord.open = accord.name === "specialOffers"),
    );
  }

  const { baseUrl } = getBaseUrlAndQuery(asPath);

  return (
    <div data-testid="product:accordion" className={styles.container}>
      {displayAccordions.map((accordion) => {
        switch (accordion.name) {
          case "description":
            return (
              <ProductDescriptionAccordionUI
                key={accordion.name}
                productType={product.__typename}
                displayCode={variant.displayCode}
                description={getDescription(product)}
                icons={product.icons || []}
                links={product.links || []}
                endorsement={endorsement}
                open={accordion.open}
                hideDescriptionLinks={
                  displayAccordions.find((a) => a.name === "buyingGuides") !==
                  undefined
                }
                showBeautySocietyLink={configProps?.beautySocietyLink === true}
              />
            );
          case "buyingGuides":
            return (
              <BuyingGuidesAccordionUI
                key={accordion.name}
                links={product.links || []}
                open={accordion.open}
              />
            );
          case "specification":
            return (
              <ProductSpecificationAccordionUI
                key={accordion.name}
                attributes={productSpecificationAttributes}
                productType={product.__typename}
                open={accordion.open}
              />
            );
          case "howToUse":
            return (
              <ProductHowToUseAccordionUI
                key={accordion.name}
                open={accordion.open}
                howtoUseContent={product?.howToUse}
              />
            );
          case "ingredients":
            return (
              <ProductIngredientsAccordionUI
                key={accordion.name}
                product={product}
                open={accordion.open}
                runtimeConfig={runtimeConfig}
              />
            );
          case "brand":
            return (
              <ProductBrandAccordionUI
                key={accordion.name}
                brand={product.brand}
                sizeGuide={product.sizeGuide}
                open={accordion.open}
                disableSizeGuide={configProps?.disableSizeGuide as boolean}
              />
            );
          case "outlet":
            return (
              <OutletAccordionUI key={accordion.name} open={accordion.open} />
            );
          case "deliveryOptions":
            return (
              <ProductDeliveryOptionsAccordionUI
                key={accordion.name}
                deliveryOptions={deliveryOptions}
                skuId={variant.aliases.skuId}
                isAvailableToOrderOrPreorder={
                  variant.availability?.availableToOrder ||
                  isAvailableToPreOrder(variant)
                }
                stockApiBaseUrl={runtimeConfig.stockApiBaseUrl}
                stockApiKey={runtimeConfig.stockApiKey}
                isInExtendedReturnsWindow={isInExtendedReturnsWindow()}
                isInStoreOnly={isInStoreOnly(variant)}
                isRetired={isRetired}
                open={accordion.open}
                browsePageHierarchy={product.browsePageHierarchy}
                showExclusionsMessaging={showExclusionsMessaging}
                showOutletReturnMessage={showOutletReturnMessage}
              />
            );
          case "reviews":
            return (
              <ProductReviewsAccordionUI
                key={accordion.name}
                productId={product.aliases.pimProductId}
                reviewsApiKey={reviewsApiKey}
                reviewsScriptUrl={reviewsScriptUrl}
                reviewSummary={product?.reviewSummary}
                productUrl={baseUrl}
                open={accordion.open}
                experiencesRawString={experiencesRawString}
              />
            );
          case "sizeAndFit":
            return (
              <ProductSizeAndFitAccordionUI
                key={accordion.name}
                attributes={sizeAndFitAttributes}
                productType={product.__typename}
                open={accordion.open}
              />
            );
          case "specialOffers":
            return (
              newPromoDrawer !== "on" && (
                <ProductSpecialOffersAccordionUI
                  key={accordion.name}
                  specialOffers={specialOffers}
                  productType={product.__typename}
                  open={accordion.open}
                />
              )
            );
          default:
            return null;
        }
      })}
    </div>
  );
};
